import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import PublicNav from "@/components/PublicNav";
import Footer from "@/components/Footer";
import capi, { mediaFull, isCustomerAuthed } from "@/lib/customerApi";
import ContactVendorDialog from "@/components/ContactVendorDialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  Loader2, ShieldCheck, MapPin, Briefcase, Star, Instagram, Globe, Mail,
  Building2, Users, Layers, ArrowLeft, BadgeCheck, Send, ImageOff,
} from "lucide-react";

const initials = (s) => (s || "?").split(" ").filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase();

export default function VendorProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [v, setV] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [canReview, setCanReview] = useState(false);
  const [rating, setRating] = useState(5);
  const [reviewText, setReviewText] = useState("");
  const [posting, setPosting] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await capi.get(`/partner/public/vendor/${id}`); setV(data); }
    catch { setNotFound(true); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (isCustomerAuthed()) capi.get(`/partner/public/vendor/${id}/can-review`).then((r) => setCanReview(r.data.eligible)).catch(() => {});
  }, [id, contactOpen]);

  const submitReview = async () => {
    if (!reviewText.trim()) { toast.error("Please write a short review."); return; }
    setPosting(true);
    try {
      await capi.post(`/partner/public/vendor/${id}/review`, { rating, review: reviewText });
      toast.success("Thanks! Your verified review is posted.");
      setReviewText(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not post review"); }
    finally { setPosting(false); }
  };

  if (notFound) return (
    <div className="min-h-screen bg-white"><PublicNav />
      <div className="max-w-2xl mx-auto px-6 py-32 text-center">
        <Building2 className="h-12 w-12 text-slate-300 mx-auto mb-4" />
        <h1 className="font-display text-2xl font-extrabold text-slate-900">Partner not found</h1>
        <p className="text-slate-500 mt-2">This partner may not be live yet.</p>
        <Button onClick={() => navigate("/partners")} className="mt-6 rounded-xl bg-[#0E9AC8] hover:bg-[#0B7EA4]">Browse Partners</Button>
      </div>
    </div>
  );
  if (!v) return <div className="min-h-screen bg-white flex items-center justify-center"><Loader2 className="h-7 w-7 animate-spin text-[#0E9AC8]" /></div>;

  const cover = v.portfolio?.[0];

  return (
    <div className="min-h-screen bg-[#FBFAF9]">
      <PublicNav />

      {/* Hero */}
      <div className="relative h-52 sm:h-64 w-full overflow-hidden bg-gradient-to-br from-[#0E9AC8] to-[#0B5563]">
        {cover && <img src={mediaFull(cover.url)} alt="" className="absolute inset-0 h-full w-full object-cover opacity-40" />}
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
        <div className="relative max-w-5xl mx-auto px-6 h-full flex items-end pb-6">
          <button onClick={() => navigate("/partners")} className="absolute top-4 left-6 flex items-center gap-1.5 text-white/90 hover:text-white text-sm"><ArrowLeft className="h-4 w-4" /> All Partners</button>
        </div>
      </div>

      <main className="max-w-5xl mx-auto px-6 -mt-16 relative pb-20">
        {/* Header card */}
        <div className="card-premium rounded-2xl p-6 sm:p-8">
          <div className="flex flex-col sm:flex-row sm:items-start gap-5">
            <div className="h-20 w-20 rounded-2xl bg-[#0E9AC8] text-white flex items-center justify-center font-display text-2xl font-extrabold shrink-0 shadow-lg">{initials(v.company_name)}</div>
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2.5">
                <h1 className="font-display text-2xl sm:text-3xl font-extrabold text-slate-900" data-testid="vp-name">{v.company_name}</h1>
                {v.verified && <span data-testid="vp-verified" className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 px-3 py-1 text-xs font-bold"><BadgeCheck className="h-4 w-4" /> Nirmantra Verified Partner</span>}
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-sm text-slate-500">
                <span className="inline-flex items-center gap-1.5"><Briefcase className="h-4 w-4 text-[#0E9AC8]" />{(v.categories || []).join(", ")}{v.subcategory ? ` · ${v.subcategory}` : ""}</span>
                <span className="inline-flex items-center gap-1.5"><MapPin className="h-4 w-4 text-[#0E9AC8]" />{v.service_cities || "—"}</span>
                {v.review_count > 0 && <span className="inline-flex items-center gap-1.5"><Star className="h-4 w-4 text-amber-400 fill-amber-400" />{v.rating} ({v.review_count})</span>}
              </div>
            </div>
            <Button data-testid="vp-contact-btn" onClick={() => setContactOpen(true)} className="rounded-xl bg-[#0E9AC8] hover:bg-[#0B7EA4] h-11 px-6 shrink-0"><Send className="h-4 w-4 mr-2" /> Contact Vendor</Button>
          </div>

          {v.intro && <p className="mt-6 text-slate-700 leading-relaxed" data-testid="vp-intro">{v.intro}</p>}

          <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Stat icon={Briefcase} label="Experience" value={v.experience_years ? `${v.experience_years} yrs` : "—"} />
            <Stat icon={Layers} label="Projects" value={v.projects_handled || "—"} />
            <Stat icon={Users} label="Team" value={v.team_size || "—"} />
            <Stat icon={Star} label="Rating" value={v.review_count > 0 ? v.rating : "New"} />
          </div>
        </div>

        <div className="mt-6 grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            {/* Portfolio */}
            <Card title="Portfolio">
              {v.portfolio?.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="vp-portfolio">
                  {v.portfolio.map((m) => (
                    <button key={m.id} onClick={() => setLightbox(mediaFull(m.url))} className="group relative aspect-square rounded-xl overflow-hidden bg-slate-100">
                      <img src={mediaFull(m.url)} alt="" className="h-full w-full object-cover transition-transform group-hover:scale-105" />
                    </button>
                  ))}
                </div>
              ) : <div className="flex flex-col items-center justify-center py-12 text-slate-400"><ImageOff className="h-8 w-8 mb-2" /><p className="text-sm">Portfolio images coming soon.</p></div>}
            </Card>

            {/* Details */}
            <Card title="About this Partner">
              <div className="grid sm:grid-cols-2 gap-x-8 gap-y-4 text-sm">
                <Detail label="Services Offered" value={v.services} />
                <Detail label="Service Areas" value={v.service_areas} />
                <Detail label="Key Team" value={v.team} />
                <Detail label="Business Email" value={v.email} icon={Mail} />
              </div>
              {v.about && v.about !== v.intro && <p className="mt-5 text-slate-600 text-sm leading-relaxed border-t border-slate-100 pt-4">{v.about}</p>}
            </Card>

            {/* Reviews */}
            <Card title={`Customer Reviews${v.review_count ? ` (${v.review_count})` : ""}`}>
              {canReview && (
                <div className="rounded-xl border border-[#0E9AC8]/25 bg-[#0E9AC8]/5 p-4 mb-5" data-testid="vp-review-form">
                  <p className="text-sm font-semibold text-slate-800 mb-2">You worked with this partner — share a verified review</p>
                  <div className="flex gap-1 mb-2">
                    {[1, 2, 3, 4, 5].map((s) => <button key={s} onClick={() => setRating(s)}><Star className={`h-6 w-6 ${s <= rating ? "text-amber-400 fill-amber-400" : "text-slate-300"}`} /></button>)}
                  </div>
                  <Textarea className="rounded-xl" value={reviewText} onChange={(e) => setReviewText(e.target.value)} placeholder="How was your experience?" />
                  <Button data-testid="vp-review-submit" onClick={submitReview} disabled={posting} className="mt-2 rounded-xl bg-[#0E9AC8] hover:bg-[#0B7EA4]">{posting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Post Verified Review"}</Button>
                </div>
              )}
              {v.reviews?.length > 0 ? (
                <div className="space-y-4">
                  {v.reviews.map((r, i) => (
                    <div key={i} className="border-b border-slate-100 pb-4 last:border-0" data-testid={`vp-review-${i}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-slate-800">{r.customer_name}</span>
                          {r.verified_customer && <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-600"><BadgeCheck className="h-3.5 w-3.5" /> Verified Customer</span>}
                        </div>
                        <div className="flex">{[1, 2, 3, 4, 5].map((s) => <Star key={s} className={`h-3.5 w-3.5 ${s <= r.rating ? "text-amber-400 fill-amber-400" : "text-slate-200"}`} />)}</div>
                      </div>
                      <p className="text-sm text-slate-600 mt-1.5">{r.review}</p>
                      {r.created_at && <p className="text-xs text-slate-400 mt-1">{new Date(r.created_at).toLocaleDateString()}</p>}
                    </div>
                  ))}
                </div>
              ) : <p className="text-sm text-slate-400">No reviews yet. Only customers who complete a project via Nirmantra can review.</p>}
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <Card title="Connect">
              <p className="text-sm text-slate-500 mb-4">Contact details are shared after a quick mobile verification — we keep your number private.</p>
              <Button data-testid="vp-contact-side" onClick={() => setContactOpen(true)} className="w-full rounded-xl bg-[#0E9AC8] hover:bg-[#0B7EA4] h-11"><Send className="h-4 w-4 mr-2" /> Contact Vendor</Button>
              <div className="mt-4 space-y-2">
                {v.instagram && <a href={v.instagram.startsWith("http") ? v.instagram : `https://${v.instagram}`} target="_blank" rel="noreferrer" className="flex items-center gap-2.5 text-sm text-slate-600 hover:text-[#0E9AC8]"><Instagram className="h-4 w-4" /> Instagram</a>}
                {v.website && <a href={v.website.startsWith("http") ? v.website : `https://${v.website}`} target="_blank" rel="noreferrer" className="flex items-center gap-2.5 text-sm text-slate-600 hover:text-[#0E9AC8]"><Globe className="h-4 w-4" /> Website</a>}
              </div>
            </Card>
            <div className="rounded-2xl bg-emerald-50 border border-emerald-200 p-5">
              <ShieldCheck className="h-6 w-6 text-emerald-600 mb-2" />
              <p className="font-display font-bold text-slate-900">Nirmantra Verified</p>
              <p className="text-sm text-slate-600 mt-1">This partner has completed Nirmantra's verification and is live in our network.</p>
            </div>
          </div>
        </div>
      </main>

      <ContactVendorDialog open={contactOpen} onOpenChange={setContactOpen} vendorId={v.id} vendorName={v.company_name}
        city={(v.service_cities || "").split(",")[0]?.trim()} category={(v.categories || [])[0]} onLead={() => setTimeout(() => { if (isCustomerAuthed()) capi.get(`/partner/public/vendor/${id}/can-review`).then((r) => setCanReview(r.data.eligible)).catch(() => {}); }, 300)} />

      {lightbox && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-6" onClick={() => setLightbox(null)}>
          <img src={lightbox} alt="" className="max-h-[90vh] max-w-full rounded-xl" />
        </div>
      )}
      <Footer />
    </div>
  );
}

const Stat = ({ icon: Icon, label, value }) => (
  <div className="rounded-xl bg-slate-50 p-4">
    <Icon className="h-4 w-4 text-[#0E9AC8] mb-1.5" />
    <p className="font-display text-lg font-extrabold text-slate-900">{value}</p>
    <p className="text-xs text-slate-500">{label}</p>
  </div>
);
const Card = ({ title, children }) => (
  <div className="card-premium rounded-2xl p-6"><h2 className="font-display text-lg font-bold text-slate-900 mb-4">{title}</h2>{children}</div>
);
const Detail = ({ label, value, icon: Icon }) => (
  <div>
    <p className="text-xs uppercase tracking-wide text-slate-400 mb-0.5">{label}</p>
    <p className="text-slate-700 flex items-center gap-1.5">{Icon && <Icon className="h-3.5 w-3.5 text-[#0E9AC8]" />}{value || "—"}</p>
  </div>
);
